import React, { useEffect, useState, useMemo, useContext } from "react";
import { Table, Tag, Button, Input, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import styled from "styled-components";
import { SearchOutlined } from "@ant-design/icons";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import {
  useSubjectiveStudentTestDetails,
  useSubjectiveTest,
} from "Pages/DRS/Context/MyBatchContext";
import { useLocation, useNavigate } from "react-router-dom";
import { IUserInfo } from "utilFunctions/CommonInterfaces/userInterface";
import { UserInfoContext, ToastContextDispatch } from "Context/CommonContext";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import {
  BASE_URL,
  EXTERNAL_TESTPLAYER_URL,
} from "utilFunctions/Constants/urlConstants";

// -- Styled Components for UI matching --
export const TableWrapper = styled.div<{ screen?: string }>`
  background: white;
  padding: ${(props) =>
    ["xs", "sm", "md"].includes(props.screen || "") ? "12px" : "24px"};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .ant-table-thead > tr > th {
    background-color: #f8fbff;
    color: #595959;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.05em;
  }

  .ant-table-row {
    &:hover {
      background-color: #fafafa;
    }
  }
`;

const StatusBadge = styled(Tag)<{ status: string }>`
  border-radius: 50px;
  padding: 0 12px;
  font-weight: 600;
  font-size: 11px;
  border: none;

  ${(props) => {
    switch (props.status?.toUpperCase()) {
      case "SUBMITTED":
        return "background-color: #e6f7ff; color: #1890ff;";
      case "IN PROGRESS":
      case "EVALUATION IN PROGRESS":
        return "background-color: #fff7e6; color: #faad14;";
      case "EVALUATED":
        return "background-color: #f6ffed; color: #52c41a;";
      case "CHALLENGED":
        return "background-color: #fff1f0; color: #ff4d4f;";
      case "NOT SUBMITTED":
        return "background-color: #f5f5f5; color: #8c8c8c;";
      case "RE_EVALUATED":
      case "RE EVALUATED":
        return "background-color: #DFE7FF; color: #4238CA;";
      default:
        return "background-color: #f5f5f5; color: #8c8c8c;";
    }
  }}
`;
const student_status = [
  {
    label: "Submitted",
    value: "SUBMITTED",
  },
  {
    label: "Not Submitted",
    value: "NOT_SUBMITTED",
  },
  {
    label: "In Progress",
    value: "IN_PROGRESS",
  },
  {
    label: "Evaluated",
    value: "EVALUATED",
  },
  {
    label: "Challenged",
    value: "CHALLENGED",
  },
  {
    label: "Re-Evaluated",
    value: "RE_EVALUATED",
  },
];
const SubjectScore = styled.span`
  font-size: 12px;
  margin-right: 12px;
  color: #8c8c8c;

  strong {
    color: #595959;
  }
`;

const ActionButton = styled(Button)<{
  buttontype?: "evaluate" | "view" | "review";
}>`
  border-radius: 4px;
  font-weight: 500;

  ${(props) => {
    if (props.buttontype === "evaluate") {
      return "background-color: #1890ff; border-color: #1890ff; color: white; &:hover { background-color: #40a9ff !important; border-color: #40a9ff !important; color: white !important; }";
    }
    if (props.buttontype === "view") {
      return "background-color: white; border-color: #d9d9d9; color: #595959;";
    }
    if (props.buttontype === "review") {
      return "background-color: #fa8c16; border-color: #fa8c16; color: white; &:hover { background-color: #ffa940 !important; border-color: #ffa940 !important; color: white !important; }";
    }
    return "";
  }}
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;

  .ant-input-affix-wrapper {
    border-radius: 8px;
    border-color: #f0f0f0;
    padding: 8px 16px;
    transition: all 0.3s;
    width: 320px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);

    &:hover,
    &-focused {
      border-color: #1890ff !important;
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.1) !important;
    }

    .ant-input {
      font-size: 14px;
      font-weight: 500;
    }

    .ant-input-prefix {
      color: #bfbfbf;
      margin-right: 8px;
    }
  }
`;

// -- Data Interface --
interface SubjectEvaluation {
  name: string;
  score: number;
  total: number;
}

interface EvaluationRecord {
  key: string;
  name: string;
  psid: string;
  status: string;
  subjects: SubjectEvaluation[];
  totalScore: string;
  action: "evaluate" | "view" | "review" | "na";
  avatarColor: string;
  submissionId: number;
  obtainedMarks: number;
}

interface ApiSectionEvaluation {
  sectionTitle: string;
  evaluatedQuestions: number;
  totalQuestions: number;
}

interface ApiStudentDetail {
  studentTestStatus: string;
  studentName: string;
  psid: string;
  obtainedMarks: number;
  sectionWiseEvaluation: ApiSectionEvaluation[];
  submissionId: number;
}

// -- Columns config --

const avatarColors = [
  "#adc6ff",
  "#ffd6e7",
  "#b7eb8f",
  "#ffccc7",
  "#efdbff",
  "#ffe58f",
];

const getActionAndStatus = (status: string) => {
  const uiStatus = (status || "").replace(/_/g, " ");
  let action: "evaluate" | "view" | "review" | "na" = "na";
  if (status === "NOT_SUBMITTED") {
    action = "na";
  } else if (
    status === "SUBMITTED" ||
    status === "IN_PROGRESS" ||
    status === "EVALUATION_IN_PROGRESS"
  ) {
    action = "evaluate";
  } else if (status === "EVALUATED") {
    action = "view";
  } else if (status === "CHALLENGED") {
    action = "review";
  } else if (status === "RE_EVALUATED" || status === "RE-EVALUATED") {
    action = "view";
  } else {
    action = "view";
  }
  return { action, uiStatus };
};

const EvaluateTable = ({
  totalMarks: marks,
  action: routeAction,
  type,
}: any) => {
  const userInfo = useContext<IUserInfo>(UserInfoContext);
  const ToastDispatch = useContext(ToastContextDispatch);
  const { subjects } = userInfo || {};
  const subjectsMap = subjects.map((s: any) => s.subjectName.toLowerCase());

  const { makeRequest: GetTestPlayerExternalUrl } = useGetApi(
    (data: any) => {
      if (data?.data?.result_url) {
        window.open(data.data.result_url, "_blank");
      }
    },
    (error: any) => {
      if (error?.message) {
        ToastDispatch({
          isOpen: true,
          type: "Error",
          message: error.message,
        });
      }
    }
  );
  const navigate = useNavigate();
  const { screen } = useWindowSize();
  const { state } = useLocation();
  const { batchId, test } =
    (state as { batchId: string; test: { id: number }; batchDetail: any }) ||
    {};

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const { fetchStudentTestDetails, studentTestDetailsData, loading } =
    useSubjectiveStudentTestDetails();
  const { subjectiveTestData } = useSubjectiveTest();

  const Test_Configuration = (subjectiveTestData as any)?.data || {};
  const totalMarks = Test_Configuration?.totalMarks || 0;
  console.log(
    "Total Marks in EvaluateTable:",
    Test_Configuration,
    marks,
    routeAction
  );
  useEffect(() => {
    if (batchId && test?.id) {
      fetchStudentTestDetails(batchId, test.id);
    }
  }, [batchId, test?.id]);

  const apiData: ApiStudentDetail[] =
    (studentTestDetailsData as any)?.data || [];

  const mappedData: any[] = useMemo(() => {
    return apiData.map((item: ApiStudentDetail, index: number) => {
      const { action, uiStatus } = getActionAndStatus(item.studentTestStatus);

      const subjects: SubjectEvaluation[] = (
        item.sectionWiseEvaluation || []
      ).map((section: ApiSectionEvaluation) => ({
        name: section.sectionTitle,
        score: section.evaluatedQuestions,
        total: section.totalQuestions,
      }));

      return {
        key: String(index),
        name: item.studentName || "N/A",
        psid: item.psid || "N/A",
        status: uiStatus,
        rawStatus: item.studentTestStatus,
        subjects: subjects,
        totalScore:
          action === "na" ? "-" : `${item.obtainedMarks || 0}/${marks}`,
        action: action,
        avatarColor: avatarColors[index % avatarColors.length],
        submissionId: item.submissionId || "",
      };
    });
  }, [apiData, totalMarks]);
  const getFilterDataByStatus = (data: any = []) => {
    console.log("Filtered Data in Results Tab:", data);
    if (type === "results") {
      data = data.filter(
        (item: any) =>
          item.rawStatus === "EVALUATED" || item.rawStatus === "RE_EVALUATED"
      );
      return data;
    }
    return data;
  };
  const filteredData = useMemo(() => {
    let data = mappedData;
    console.log(
      "Filtered Data in Results Tab:",
      data,
      searchText,
      statusFilter
    );
    if (statusFilter) {
      data = data.filter((item) => {
        if (statusFilter === "IN_PROGRESS") {
          return (
            item.rawStatus === "IN_PROGRESS" ||
            item.rawStatus === "EVALUATION_IN_PROGRESS"
          );
        }
        if (statusFilter === "RE_EVALUATED") {
          return (
            item.rawStatus === "RE_EVALUATED" ||
            item.rawStatus === "RE-EVALUATED"
          );
        }
        return item.rawStatus === statusFilter;
      });
    }

    if (!searchText) return getFilterDataByStatus(data);

    return getFilterDataByStatus(
      data.filter(
        (item) =>
          String(item.psid).toLowerCase().includes(searchText.toLowerCase()) ||
          String(item.name).toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [mappedData, searchText, statusFilter]);
  const routeToPerformance = (item: any) => {
    navigate("/admin/performance/" + item.psid, {
      replace: false,
      state: {
        selectedStudent: item,
        referrer: location.pathname,
        myBatchRedirectSelect: "Student Details",
      },
    });
  };
  const isSubjectNameInSubjectsMap = (subjectName: string): string => {
    if (!subjectName) return "";
    const value = subjectsMap.find((s) =>
      subjectName.toLowerCase().includes(s)
    );
    return value ? value.toUpperCase() : subjectName.toUpperCase();
  };
  const columns: ColumnsType<EvaluationRecord> = [
    {
      title: "STUDENT NAME",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      render: (text: string, record: EvaluationRecord) => (
        <span
          className="tableIdStyle"
          onClick={() => routeToPerformance(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "PSID",
      dataIndex: "psid",
      key: "psid",
      render: (text: string) => (
        <span style={{ color: "#595959" }}>{text}</span>
      ),
    },
    {
      title: "STUDENT TEST STATUS",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <StatusBadge status={status}>{status}</StatusBadge>
      ),
    },
    {
      title: "SECTION WISE EVALUATION",
      dataIndex: "subjects",
      key: "subjects",
      render: (subjects: SubjectEvaluation[]) => (
        <div>
          {subjects.length > 0 ? (
            subjects.map((s: SubjectEvaluation, i: number) => (
              <SubjectScore key={i}>
                <strong>
                  {s?.name && isSubjectNameInSubjectsMap(s.name)}:
                </strong>{" "}
                {s.score}/{s.total}
              </SubjectScore>
            ))
          ) : (
            <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>
              Not available
            </span>
          )}
        </div>
      ),
    },
    {
      title: "TOTAL MARKS",
      dataIndex: "totalScore",
      key: "totalScore",
      render: (totalScore) => (
        <span style={{ color: "#595959" }}>{totalScore}</span>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      fixed: "right",
      render: (_, record: EvaluationRecord) => {
        console.log(
          "Rendering Action Button for record:",
          record,
          "with routeAction:",
          routeAction
        );
        if (type === "results") {
          return (
            <Button
              type="primary"
              onClick={() => {
                const url =
                  BASE_URL +
                  EXTERNAL_TESTPLAYER_URL +
                  "?user_id=" +
                  record.psid +
                  "&external_schedule_id=" +
                  (test as any)?.externalScheduleId;
                GetTestPlayerExternalUrl(url);
              }}
            >
              View
            </Button>
          );
        }
        if (record.action != "na" && routeAction === "view") {
          return (
            <ActionButton
              buttontype="view"
              onClick={() => {
                navigate("/admin/student-evaluation/" + record.psid, {
                  replace: false,
                  state: {
                    ...state,
                    record: { ...record, action: "view" },
                    routeAction: routeAction,
                    totalMarks: marks,
                  },
                });
              }}
            >
              View
            </ActionButton>
          );
        }
        if (record.action === "evaluate" || record.action === "view") {
          return (
            <ActionButton
              buttontype="evaluate"
              onClick={() => {
                navigate("/admin/student-evaluation/" + record.psid, {
                  replace: false,
                  state: {
                    ...state,
                    record,
                    totalMarks: marks,
                  },
                });
              }}
            >
              {record.action === "view" ? "View" : "Evaluate"}
            </ActionButton>
          );
        }
        if (record.action === "review") {
          return (
            <ActionButton
              buttontype="review"
              onClick={() => {
                navigate("/admin/student-evaluation/" + record.psid, {
                  replace: false,
                  state: {
                    ...state,
                    record,
                    totalMarks: marks,
                    action: "evaluate",
                  },
                });
              }}
            >
              Review
            </ActionButton>
          );
        }
        return <span style={{ color: "#bfbfbf", marginRight: 8 }}>NA</span>;
      },
    },
  ];
  console.log("Mapped Data for Table:", filteredData);
  return (
    <TableWrapper screen={screen}>
      <SearchContainer>
        {/* 
        add studentTestStatus filter over here using a select dropdown
        */}
        <Select
          placeholder="Filter by Status"
          allowClear
          style={{ width: 150, marginRight: 16 }}
          value={statusFilter || undefined}
          onChange={(value) => setStatusFilter(value || "")}
          options={student_status}
        ></Select>
        <Input
          placeholder="Search by PSID or Name"
          prefix={<SearchOutlined />}
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </SearchContainer>

      <Table
        columns={columns}
        dataSource={filteredData}
        size="small"
        loading={loading}
        pagination={false}
        scroll={{ x: "max-content" }}
      />
    </TableWrapper>
  );
};

export default EvaluateTable;
